import {goalMatchesSearch, goalScope} from '../my-objective/my-goals-v2.utils';
import {
  AlignmentForest,
  AlignmentNode,
  AlignmentSpotlightState,
} from './alignment.types';

export const ALIGNMENT_TRAVERSAL_LIMIT = 100;

export const alignmentEntityId = (value: any): string =>
  typeof value === 'string'
    ? value
    : value?.id || value?._id || value?.value || '';

export const alignmentParentId = (goal: any): string =>
  alignmentEntityId(goal?.goalAlignment);

const uniqueGoals = (goals: any[]) => {
  const goalsById = new Map<string, any>();
  goals.forEach((goal) => {
    const id = alignmentEntityId(goal);
    if (id && !goalsById.has(id)) goalsById.set(id, goal);
  });
  return goalsById;
};

const createNodeBuilder = ({
  goalsById,
  parentById,
  childrenById,
}: Pick<AlignmentForest, 'goalsById' | 'parentById' | 'childrenById'>) => {
  const claimed = new Set<string>();

  const buildNode = (
    id: string,
    path = new Set<string>(),
    depth = 0,
  ): AlignmentNode | null => {
    const goal = goalsById.get(id);
    if (!goal || claimed.has(id) || path.has(id)) return null;

    claimed.add(id);
    const nextPath = new Set(path).add(id);
    const children =
      depth >= ALIGNMENT_TRAVERSAL_LIMIT
        ? []
        : ((childrenById.get(id) || [])
            .map((childId) => buildNode(childId, nextPath, depth + 1))
            .filter(Boolean) as AlignmentNode[]);

    return {
      id,
      parentId: parentById.get(id) || '',
      goal,
      children,
    };
  };

  return {buildNode, claimed};
};

export const buildAlignmentForest = (goals: any[]): AlignmentForest => {
  const goalsById = uniqueGoals(goals.filter((goal) => !goal?.isKpi));
  const parentById = new Map<string, string>();
  const childrenById = new Map<string, string[]>();

  goalsById.forEach((goal, id) => {
    const parentId = alignmentParentId(goal);
    parentById.set(id, parentId);
    if (!parentId || !goalsById.has(parentId)) return;
    childrenById.set(parentId, [...(childrenById.get(parentId) || []), id]);
  });

  const alignedIds = new Set<string>();
  const companyRootIds = Array.from(goalsById.entries())
    .filter(
      ([id, goal]) => goalScope(goal) === 'company' && !parentById.get(id),
    )
    .map(([id]) => id);

  const markAligned = (id: string, path = new Set<string>(), depth = 0) => {
    if (
      alignedIds.has(id) ||
      path.has(id) ||
      depth > ALIGNMENT_TRAVERSAL_LIMIT
    ) {
      return;
    }
    alignedIds.add(id);
    const nextPath = new Set(path).add(id);
    (childrenById.get(id) || []).forEach((childId) =>
      markAligned(childId, nextPath, depth + 1),
    );
  };
  companyRootIds.forEach((id) => markAligned(id));

  const unalignedIds = new Set(
    Array.from(goalsById.keys()).filter((id) => !alignedIds.has(id)),
  );

  const alignedBuilder = createNodeBuilder({
    goalsById,
    parentById,
    childrenById,
  });
  const alignedRoots = companyRootIds
    .map((id) => alignedBuilder.buildNode(id))
    .filter(Boolean) as AlignmentNode[];

  const unalignedBuilder = createNodeBuilder({
    goalsById,
    parentById,
    childrenById,
  });
  const naturalUnalignedRoots = Array.from(unalignedIds).filter((id) => {
    const parentId = parentById.get(id);
    return !parentId || !unalignedIds.has(parentId);
  });
  const unalignedRoots = naturalUnalignedRoots
    .map((id) => unalignedBuilder.buildNode(id))
    .filter(Boolean) as AlignmentNode[];

  // A cycle has no natural root. Pick the first still-unclaimed node so the
  // component remains visible once, without following the loop forever.
  Array.from(unalignedIds).forEach((id) => {
    if (unalignedBuilder.claimed.has(id)) return;
    const node = unalignedBuilder.buildNode(id);
    if (node) unalignedRoots.push(node);
  });

  return {
    alignedRoots,
    unalignedRoots,
    alignedIds,
    unalignedIds,
    goalsById,
    parentById,
    childrenById,
  };
};

export const alignmentAncestorClosure = (
  forest: AlignmentForest,
  goalIds: Iterable<string>,
) => {
  const visible = new Set<string>();

  Array.from(goalIds).forEach((startId) => {
    let id = startId;
    const visited = new Set<string>();
    let depth = 0;

    while (
      id &&
      forest.goalsById.has(id) &&
      !visited.has(id) &&
      depth <= ALIGNMENT_TRAVERSAL_LIMIT
    ) {
      visible.add(id);
      visited.add(id);
      id = forest.parentById.get(id) || '';
      depth += 1;
    }
  });

  return visible;
};

const pruneNode = (
  node: AlignmentNode,
  visibleIds: Set<string>,
): AlignmentNode | null => {
  if (!visibleIds.has(node.id)) return null;
  return {
    ...node,
    children: node.children
      .map((child) => pruneNode(child, visibleIds))
      .filter(Boolean) as AlignmentNode[],
  };
};

export const pruneAlignmentForest = (
  forest: AlignmentForest,
  visibleIds: Set<string>,
): AlignmentForest => ({
  ...forest,
  alignedRoots: forest.alignedRoots
    .map((root) => pruneNode(root, visibleIds))
    .filter(Boolean) as AlignmentNode[],
  unalignedRoots: forest.unalignedRoots
    .map((root) => pruneNode(root, visibleIds))
    .filter(Boolean) as AlignmentNode[],
});

export const alignmentSearchIds = (forest: AlignmentForest, search: string) => {
  const query = search.trim();
  if (!query) return new Set(forest.goalsById.keys());
  const matches = Array.from(forest.goalsById.entries())
    .filter(([, goal]) => goalMatchesSearch(goal, query))
    .map(([id]) => id);
  return alignmentAncestorClosure(forest, matches);
};

export const alignmentSpotlight = (
  forest: AlignmentForest,
  ownerIds: Iterable<string>,
) => {
  const spotlightOwners = new Set(Array.from(ownerIds).filter(Boolean));
  const ownedIds = new Set<string>();
  forest.goalsById.forEach((goal, id) => {
    if (spotlightOwners.has(alignmentEntityId(goal?.assignee))) {
      ownedIds.add(id);
    }
  });

  const relatedIds = alignmentAncestorClosure(forest, ownedIds);
  const queue = Array.from(ownedIds);
  let visits = 0;
  while (
    queue.length &&
    visits <= forest.goalsById.size + ALIGNMENT_TRAVERSAL_LIMIT
  ) {
    const id = queue.shift() as string;
    (forest.childrenById.get(id) || []).forEach((childId) => {
      if (relatedIds.has(childId)) return;
      relatedIds.add(childId);
      queue.push(childId);
    });
    visits += 1;
  }

  const stateFor = (id: string): AlignmentSpotlightState =>
    ownedIds.has(id) ? 'owned' : relatedIds.has(id) ? 'related' : 'dimmed';

  return {ownedIds, relatedIds, stateFor};
};

export const expandableAlignmentIds = (forest: AlignmentForest) => {
  const ids = new Set<string>();
  const visit = (node: AlignmentNode) => {
    if (node.children.length) ids.add(node.id);
    node.children.forEach(visit);
  };
  [...forest.alignedRoots, ...forest.unalignedRoots].forEach(visit);
  return ids;
};
